import React from 'react'
import { useBulkOperations, BulkOperation } from '@/hooks/useBulkOperations'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/brand/LoadingSpinner'
import { formatDistanceToNow } from 'date-fns'
import { CheckCircle, XCircle, Clock, Settings, Users, Trash2, Edit, RefreshCw } from 'lucide-react'

const getOperationIcon = (operationType: string) => {
  switch (operationType) {
    case 'bulk_edit':
      return <Edit className="h-4 w-4" />
    case 'individual_update':
      return <Edit className="h-4 w-4" />
    case 'client_assignment':
      return <Users className="h-4 w-4" />
    case 'provider_update':
      return <Settings className="h-4 w-4" />
    case 'warmup_toggle':
      return <Settings className="h-4 w-4" />
    case 'bulk_delete':
      return <Trash2 className="h-4 w-4" />
    default:
      return <Settings className="h-4 w-4" />
  }
}

const getOperationLabel = (operationType: string) => {
  switch (operationType) {
    case 'bulk_edit':
      return 'Bulk Settings Update'
    case 'individual_update':
      return 'Individual Mailbox Update'
    case 'client_assignment':
      return 'Client Assignment'
    case 'provider_update':
      return 'Provider Update'
    case 'warmup_toggle':
      return 'Warmup Toggle'
    case 'bulk_delete':
      return 'Bulk Delete'
    default:
      return operationType.replace(/_/g, ' ').toUpperCase()
  }
}

function BulkOperationItem({ operation }: { operation: BulkOperation }) {
  const isCompleted = operation.completed_at !== null
  const isSuccess = operation.failed_count === 0
  const timeAgo = formatDistanceToNow(new Date(operation.started_at), { addSuffix: true })

  return (
    <div className="flex items-start gap-3 p-4 border-b border-border/50 last:border-b-0">
      <div className="flex-shrink-0 mt-1">
        {getOperationIcon(operation.operation_type)}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm">
            {getOperationLabel(operation.operation_type)}
          </span>
          {isCompleted ? (
            <Badge variant={isSuccess ? "default" : "destructive"} className="text-xs">
              {isSuccess ? (
                <><CheckCircle className="h-3 w-3 mr-1" />Completed</>
              ) : (
                <><XCircle className="h-3 w-3 mr-1" />Partial</>
              )}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              In Progress
            </Badge>
          )}
        </div>
        
        <div className="text-xs text-muted-foreground mb-2">
          {timeAgo} • {operation.performed_by || 'System'}
        </div>
        
        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground">
            Total: <span className="font-medium text-foreground">{operation.total_selected}</span>
          </span>
          <span className="text-green-600">
            Success: <span className="font-medium">{operation.successful_count}</span>
          </span>
          {operation.failed_count > 0 && (
            <span className="text-red-600">
              Failed: <span className="font-medium">{operation.failed_count}</span>
            </span>
          )}
        </div>
        
        {operation.operation_details && Object.keys(operation.operation_details).length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            {Object.entries(operation.operation_details)
              .filter(([key]) => key !== 'mailbox_email') // Don't show email in details since it's already in title
              .map(([key, value]) => (
                <div key={key} className="mb-1">
                  <span className="font-medium text-foreground">{key.replace(/_/g, ' ')}</span>: {String(value)}
                </div>
              ))}
          </div>
        )}
        
        {operation.errors && operation.errors.length > 0 && (
          <div className="mt-2">
            <details className="text-xs">
              <summary className="text-red-600 cursor-pointer">
                {operation.errors.length} error(s)
              </summary>
              <div className="mt-1 text-red-600 bg-red-50 p-2 rounded">
                {operation.errors.map((error, idx) => (
                  <div key={idx}>{error}</div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  )
}

export function BulkOperationsList() {
  const { data: operations = [], isLoading, error, refetch, isFetching } = useBulkOperations()

  const handleRefresh = () => {
    refetch()
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Update History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-600">Failed to load operations</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border/50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-3 text-primary">
            <div className="p-2 rounded-lg bg-primary/10">
              <Settings className="h-5 w-5" />
            </div>
            Update History
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
            className="h-8 px-2 border-primary/20 hover:bg-primary/10"
          >
            <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : operations.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground text-center">
            No bulk operations yet
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {operations.map((operation) => (
              <BulkOperationItem key={operation.id} operation={operation} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}